'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/store/authStore';

type AuthErrorKind = 'session_expired' | 'forbidden' | null;

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isAuthError: boolean;
  authErrorKind: AuthErrorKind;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: Error;
    resetError: () => void;
    isAuthError: boolean;
    authErrorKind: AuthErrorKind;
  }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Next.js implements redirect() and notFound() by THROWING control-flow errors.
 * In Next.js 16.x, these are identified by a `digest` with prefixes:
 * - "NEXT_REDIRECT;..." — for redirect() calls
 * - "NEXT_HTTP_ERROR_FALLBACK;..." — for notFound(), forbidden(), unauthorized() etc.
 *
 * A custom error boundary MUST re-throw these so the framework can handle them;
 * otherwise a server-component redirect()/notFound() (e.g. the role gate on SMR
 * stage pages like /smr/:id/specialist-final) gets swallowed and rendered as a
 * generic "Something Went Wrong" page instead of performing the redirect.
 */
function isNextControlFlowError(error: unknown): boolean {
  const digest = (error as { digest?: unknown } | null | undefined)?.digest;
  if (typeof digest !== 'string') return false;
  return (
    digest.startsWith('NEXT_REDIRECT') ||
    digest.startsWith('NEXT_HTTP_ERROR_FALLBACK')
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false,
      authErrorKind: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Never swallow Next.js control-flow errors — re-throw so the framework can
    // perform the redirect()/notFound() instead of showing the error fallback.
    if (isNextControlFlowError(error)) throw error;

    // Prefer the structured AppError statusCode when available; only fall
    // back to substring matching the message (which can false-positive on
    // legitimate strings like "Two-factor Authentication required" etc).
    const statusCode =
      error instanceof Error && 'statusCode' in error
        ? (error as { statusCode?: number }).statusCode
        : undefined;

    let authErrorKind: AuthErrorKind = null;
    if (statusCode === 401) {
      authErrorKind = 'session_expired';
    } else if (statusCode === 403) {
      authErrorKind = 'forbidden';
    } else if (statusCode === undefined) {
      // Loose fallback for non-AppError throws. Distinguish 403 from 401 so
      // a permission-denied error doesn't masquerade as a dead session.
      if (error.message.includes('403') || /forbidden/i.test(error.message)) {
        authErrorKind = 'forbidden';
      } else if (
        error.message.includes('401') ||
        /unauthorized|session expired/i.test(error.message)
      ) {
        authErrorKind = 'session_expired';
      }
    }

    return {
      hasError: true,
      error,
      isAuthError: authErrorKind !== null,
      authErrorKind
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false,
      authErrorKind: null
    });
  };

  override render() {
    if (this.state.hasError) {
      const { error, isAuthError, authErrorKind } = this.state;

      // If custom fallback is provided, use it
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={error!}
            resetError={this.resetError}
            isAuthError={isAuthError}
            authErrorKind={authErrorKind}
          />
        );
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={error!}
          resetError={this.resetError}
          isAuthError={isAuthError}
          authErrorKind={authErrorKind}
        />
      );
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error;
  resetError: () => void;
  isAuthError: boolean;
  authErrorKind: AuthErrorKind;
}

function DefaultErrorFallback({
  error,
  resetError,
  authErrorKind
}: DefaultErrorFallbackProps) {
  const { clearAuth } = useAuth();

  const handleSignIn = () => {
    clearAuth();
    window.location.href = '/login?reason=session-expired';
  };

  const handleGoBack = () => {
    // Reset the boundary first so navigating back to a working page works.
    resetError();
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/dashboard/overview';
    }
  };

  // Pick the appropriate UX for the error kind. A 403 is NOT a dead session
  // — clearAuth() on it would log the user out of a perfectly valid session.
  const getErrorMessage = (): {
    title: string;
    description: string;
    primaryLabel: string;
    primaryAction: () => void;
    showGoBack: boolean;
  } => {
    if (authErrorKind === 'forbidden') {
      return {
        title: 'Access Denied',
        description:
          "You don't have permission to view or perform this action. Your session is still active.",
        primaryLabel: 'Go Back',
        primaryAction: handleGoBack,
        showGoBack: false
      };
    }
    if (authErrorKind === 'session_expired') {
      return {
        title: 'Session Expired',
        description:
          'Your session has ended. Please sign in again to continue.',
        primaryLabel: 'Sign In',
        primaryAction: handleSignIn,
        showGoBack: true
      };
    }
    return {
      title: 'Something Went Wrong',
      description:
        'An unexpected error occurred. Please try again or go back to the dashboard.',
      primaryLabel: 'Go Back',
      primaryAction: handleGoBack,
      showGoBack: false
    };
  };

  const { title, description, primaryLabel, primaryAction, showGoBack } =
    getErrorMessage();

  return (
    <div className='flex min-h-[400px] items-center justify-center p-4'>
      <Card className='w-full max-w-md'>
        <CardHeader className='text-center'>
          <div className='bg-destructive/10 dark:bg-destructive/20 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full'>
            <AlertTriangle className='text-destructive h-6 w-6' />
          </div>
          <CardTitle className='text-lg'>{title}</CardTitle>
          <CardDescription className='text-center'>
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Error details for development */}
          {process.env.NODE_ENV === 'development' && (
            <div className='space-y-2'>
              <Badge variant='destructive' className='text-xs'>
                Development Mode
              </Badge>
              <details className='text-muted-foreground text-xs'>
                <summary className='cursor-pointer font-medium'>
                  Error Details
                </summary>
                <pre className='bg-muted mt-2 rounded p-2 break-words whitespace-pre-wrap'>
                  {error.message}
                </pre>
              </details>
            </div>
          )}

          <div className='flex flex-col gap-2'>
            <Button onClick={primaryAction} className='w-full'>
              {primaryLabel}
            </Button>
            {showGoBack && (
              <Button
                onClick={handleGoBack}
                variant='outline'
                className='w-full'
              >
                Go Back
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
